import { TextField, Typography } from "@mui/material";
import { useForm } from "../../hooks/useForm/useForm";
import { FormProps } from "../form-types";
import { Database } from "../../../../lib/supabase";
import { TitleAndNameGroup } from "../form-input-components/groups/title-and-name.group";
import { useSessionContext } from "../../../../lib/contexts/SessionContext";
import { useProfileContext } from "../../../../lib/contexts/ProfileContext";
import { CustomTextField } from "../form-input-components/custom-input-components/custom-text-field.component";
import {
	ProfileEntityType,
	SupabaseTableEnum,
} from "../../../../lib/supabase/supabaseTypes";
import { Button } from "@/dentlab/src/components/ui/button";
import { Mail } from "lucide-react";

/**
 * ProfileForm_USER - A form to update a user's profile from the user profile page.
 * @param param0
 * @returns
 */
export const ProfileForm_USER: React.FC<FormProps> = ({
	onSave = (profile: ProfileEntityType) => {},
	submitRef = null,
}) => {
	const supabaseTable = SupabaseTableEnum.PROFILES;

	const {
		session: { userId, userEmail },
	}: any = useSessionContext();
	const { setProfile }: any = useProfileContext;

	const {
		handleInputChange,
		handleUpdate,
		formData,
		formErrors,
		handleValueChange,
	} = useForm<Database["public"]["Tables"]["profiles"]["Update"]>(
		{ modified_by_user_at: new Date() },
		supabaseTable,
		true,
		userId,
		"Profil"
	);

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const { success, error, data } = await handleUpdate(event, userId);
		if (success && data) {
			setProfile(formData);
			const profile = data[0];
			onSave(profile);
		}
	};

	return (
		<>
			<form onSubmit={onSubmit} className="space-y-5">
				<button
					ref={submitRef}
					type="submit"
					style={{ display: "none" }}
				/>
				
				{/* Email Display */}
				<div className="flex items-center gap-2 mb-6 p-3 bg-gray-50 rounded-md">
					<Mail className="h-4 w-4 text-gray-500" />
					<Typography variant="body2" className="text-gray-700">{userEmail}</Typography>
				</div>
				
				{/* Name Fields */}
				<div className="space-y-4">
					<TitleAndNameGroup
						formData={formData}
						formErrors={formErrors}
						onChange={handleInputChange}
						onValueChange={(args: any) =>
							handleValueChange(args.name, args.value)
						}
						size="small"
					/>
					
					<TextField
						onChange={handleInputChange}
						value={formData.display_name || ""}
						label="Benutzername"
						name="display_name"
						size="small"
						fullWidth
						variant="outlined"
						className="mt-4"
					/>
					
					<TextField
						onChange={handleInputChange}
						value={formData.additional_information || ""}
						name="additional_information"
						label="Zusatz"
						size="small"
						fullWidth
						variant="outlined"
					/>
				</div>
				
				{/* Contact Information */}
				<div className="pt-2">
					<h3 className="text-sm font-medium text-gray-700 mb-3">Adresse</h3>
					<div className="space-y-4">
						<TextField
							onChange={handleInputChange}
							value={formData.street || ""}
							name="street"
							label="Strasse"
							size="small"
							fullWidth
							variant="outlined"
						/>
						
						<TextField
							onChange={handleInputChange}
							value={formData.city || ""}
							name="city"
							label="Stadt"
							size="small"
							fullWidth
							variant="outlined"
						/>
					</div>
				</div>
				
				{/* Phone Numbers */}
				<div className="pt-2">
					<h3 className="text-sm font-medium text-gray-700 mb-3">Kontakt</h3>
					<div className="space-y-4">
						<CustomTextField
							onChange={handleInputChange}
							value={formData.phone_personal || ""}
							name="phone_personal"
							label="Telefon (privat)"
							size="small"
							fullWidth
							validationError={formErrors.phone_personal}
						/>
						
						<CustomTextField
							onChange={handleInputChange}
							value={formData.phone_mobile || ""}
							name="phone_mobile"
							label="Telefon (mobil)"
							size="small"
							fullWidth
							validationError={formErrors.phone_mobile}
						/>
					</div>
				</div>
				
				<div className="pt-4">
					<Button type="submit" variant="outline">
						Speichern
					</Button>
				</div>
			</form>
		</>
	);
};